/**
 * Search, quick filters and dropdowns — docs/06-UI-SPEC.md §11.2.
 *
 * One row a teacher can use without reading anything: type to search, tap a chip for
 * a period, or pick a student or a topic. Whatever is narrowing the list is shown
 * underneath as a chip with an × on it, so nothing is ever hidden.
 */

import {
  activeChips,
  isUnfiltered,
  NO_FILTERS,
  toggleQuick,
  withoutChip,
  type LectureFilters,
  type QuickFilter,
} from '../../lecture-filters.js';

const QUICK: readonly { readonly value: QuickFilter; readonly label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This week' },
  { value: 'month', label: 'This month' },
  { value: 'starred', label: 'Starred' },
];

const CONTROL =
  'h-touch rounded-md border border-border bg-surface px-3 text-base text-text-primary';

export interface FilterBarProps {
  readonly filters: LectureFilters;
  readonly topics: readonly string[];
  readonly students: readonly string[];
  readonly onChange: (filters: LectureFilters) => void;
}

function QuickChips({ filters, onChange }: Omit<FilterBarProps, 'topics' | 'students'>) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {QUICK.map((chip) => (
        <button
          key={chip.value}
          type="button"
          aria-pressed={filters.quick === chip.value}
          onClick={() => {
            onChange(toggleQuick(filters, chip.value, Date.now()));
          }}
          className={`h-touch rounded-full border px-4 text-sm font-medium transition duration-fast ease-out ${
            filters.quick === chip.value
              ? 'border-primary bg-primary-subtle text-text-primary'
              : 'border-border bg-surface text-text-secondary hover:bg-surface-hover'
          }`}
        >
          {chip.label}
        </button>
      ))}
    </div>
  );
}

function Picker({
  label,
  value,
  options,
  onPick,
}: {
  readonly label: string;
  readonly value: string | null;
  readonly options: readonly string[];
  readonly onPick: (value: string | null) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm text-text-secondary">
      <span>{label}</span>
      <select
        value={value ?? ''}
        onChange={(event) => {
          onPick(event.target.value === '' ? null : event.target.value);
        }}
        className={CONTROL}
      >
        <option value="">Any</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}

function ActiveChips({ filters, onChange }: Omit<FilterBarProps, 'topics' | 'students'>) {
  const chips = activeChips(filters);
  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-text-secondary">Showing:</span>
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          onClick={() => {
            onChange(withoutChip(filters, chip.key));
          }}
          className="flex h-touch items-center gap-2 rounded-full border border-border bg-primary-subtle px-3 text-sm text-text-primary transition duration-fast hover:bg-surface-hover"
        >
          <span>{chip.label}</span>
          <span aria-hidden>×</span>
          <span className="sr-only">Remove this filter</span>
        </button>
      ))}
    </div>
  );
}

export function FilterBar({ filters, topics, students, onChange }: FilterBarProps) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={filters.search}
          placeholder="Search lessons…"
          aria-label="Search lessons"
          onChange={(event) => {
            onChange({ ...filters, search: event.target.value });
          }}
          className={`${CONTROL} min-w-[240px] flex-1`}
        />
        <QuickChips filters={filters} onChange={onChange} />
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <Picker
          label="Student"
          value={filters.student}
          options={students}
          onPick={(student) => {
            onChange({ ...filters, student });
          }}
        />
        <Picker
          label="Topic"
          value={filters.topic}
          options={topics}
          onPick={(topic) => {
            onChange({ ...filters, topic });
          }}
        />
        {isUnfiltered(filters) ? null : (
          <button
            type="button"
            onClick={() => {
              onChange(NO_FILTERS);
            }}
            className="h-touch rounded-md px-3 text-sm font-medium text-text-secondary underline transition-colors duration-fast hover:text-text-primary"
          >
            Clear all
          </button>
        )}
      </div>

      <ActiveChips filters={filters} onChange={onChange} />
    </div>
  );
}
